import { coerceWidgetData, WidgetView } from "@docit/doc-widgets";
import { Node } from "@tiptap/core";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

/**
 * Widgets and charts as document blocks — data-carrying figures, not pictures.
 *
 * The design note the shared registry carries is the whole reason these are nodes rather than
 * images: a rendered SVG "cannot be re-derived, re-checked against the run that produced it,
 * or re-rendered in a new theme… a drawn chart is an assertion, a rendered one is a view of
 * recorded values." So what lives in the document is the VALUES, and `source {runId, nodeId}`
 * says which thinker produced them.
 *
 * ── The fence is the on-disk truth ──────────────────────────────────────────────────────
 * A widget is a fenced code block whose language is `widget` (or `chart`, the deployed legacy
 * carrier) and whose body is the JSON payload. That matters more here than upstream, where
 * `parseMarkdown` was dead code — an `ExtensionManager` keyed on a token type markdown-it
 * never emitted, so only the SERVER ever turned a fence into a node. Slate is markdown-in and
 * markdown-out, so without a real rule a widget would render as a code block forever.
 *
 * ── `data` is a STRING, deliberately ────────────────────────────────────────────────────
 * y-prosemirror compares node attrs by IDENTITY, so an object-valued attr is rewritten into
 * the CRDT on every single transaction — O(n) state growth per keystroke. Both lanes store the
 * payload as a JSON string; `coerceWidgetData` accepts either. Keeping the string VERBATIM is
 * also what makes the round trip byte-identical: nothing re-serialises somebody's formatting.
 */

export interface WidgetAttrs {
  /** The payload, as the JSON text that was in the fence. Never an object (see above). */
  data: string;
  /** Text that keeps the figure visible to search and screen readers. */
  alt: string;
}

const FENCE_LANGUAGES = new Set(["widget", "chart"]);

function WidgetNodeView(props: NodeViewProps) {
  const raw = props.node.attrs.data as unknown;
  const alt = String(props.node.attrs.alt ?? "");
  // One registry, one component, both lanes: the v2 NodeView mounts this exact `WidgetView`.
  // A payload the registry cannot validate renders as a framed reason — the figure is lost,
  // the document is not.
  return (
    <NodeViewWrapper
      as="div"
      className="docit-widget-nodeview"
      data-alt={alt}
      contentEditable={false}
    >
      <WidgetView data={coerceWidgetData(raw) as never} />
    </NodeViewWrapper>
  );
}

function makeWidgetNode(name: "widget" | "chart") {
  return Node.create({
    name,
    group: "block",
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
      return {
        data: { default: "{}" },
        alt: { default: "" },
      };
    },

    parseHTML() {
      return [{ tag: `div[data-${name}]` }];
    },

    renderHTML({ HTMLAttributes }) {
      return [
        "div",
        {
          [`data-${name}`]: "",
          "data-payload": String(HTMLAttributes.data ?? "{}"),
          "data-alt": String(HTMLAttributes.alt ?? ""),
        },
      ];
    },

    addNodeView() {
      return ReactNodeViewRenderer(WidgetNodeView);
    },

    // Rendering keys on the extension NAME, not on a token, so there is no ordering contest
    // here and it stays where the node is.
    renderMarkdown(node) {
      // Byte-identical: the fence body is the attr, verbatim. Re-serialising the parsed object
      // here is how key order and whitespace drift on every save.
      const data = String((node.attrs as { data?: unknown } | undefined)?.data ?? "{}");
      return `\`\`\`${name}\n${data}\n\`\`\``;
    },
  });
}

/** The `widget` node — the current carrier for all six registry types. */
export const WidgetNode = makeWidgetNode("widget");

/**
 * The `chart` node — the DEPLOYED legacy carrier, kept because documents already contain it.
 * `adaptLegacyChart` (inside `coerceWidgetData`'s sibling in the registry) maps its older
 * `kind` field onto `type`, so both render through the same component.
 */
export const ChartNode = makeWidgetNode("chart");

/** True when `text` parses as a JSON object — the only thing a widget payload can be. */
export function isJsonObject(text: string): boolean {
  try {
    const parsed = JSON.parse(text);
    return Boolean(parsed) && typeof parsed === "object" && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

export { FENCE_LANGUAGES };
